// ============================================================================
// BRANCH SERVICE
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { branchEndpoints, BRANCH_BASE_PATH } from '../endpoints/branch.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  Branch,
  BranchListItem,
  CreateBranchRequest,
  UpdateBranchRequest,
  BranchSearchRequest,
} from '../types/branch.types';

class BranchService {
  /** Paginated list — transforms backend PagedResult to frontend PaginatedResponse. */
  async getAll(params?: BranchSearchRequest): Promise<PaginatedResponse<BranchListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(BRANCH_BASE_PATH, params)
      : branchEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<Branch>> {
    const response = await get<ApiResponse<Branch>>(branchEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<Branch>> {
    const response = await get<ApiResponse<Branch>>(branchEndpoints.getByCode(code));
    return response.data;
  }

  async create(data: CreateBranchRequest): Promise<ApiResponse<Branch>> {
    const response = await post<ApiResponse<Branch>>(branchEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateBranchRequest): Promise<ApiResponse<Branch>> {
    const response = await put<ApiResponse<Branch>>(branchEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(branchEndpoints.delete(id));
    return response.data;
  }

  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    const endpoint = excludeId
      ? `${branchEndpoints.validateCode(code)}?excludeId=${excludeId}`
      : branchEndpoints.validateCode(code);
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

export const branchService = new BranchService();
export default branchService;
